import { atom } from "jotai";
import { RouterProvider, createHashRouter, redirect } from "react-router-dom";
import PaymentMethod from "./routes/PaymentMethod";
import Summary from "./routes/Summary";
import ThankYou from "./routes/ThankYou";
import Fail from "./routes/SendFailed";
import { FormModel, TravelSearchModel } from "./types/FormTypes";
import Root from "./routes/root";
import Delay from "./routes/Delay";
import Intro from "./routes/Intro";
import TravelSearch from "./routes/TravelSearch";
import Compensation from "./routes/Compensation";
import CompensationForTaxi from "./routes/CompensationForTaxi";
import CompensationForCar from "./routes/CompensationForCar";
import RefundValueCode from "./routes/RefundValueCode";
import RefundBankDeposit from "./routes/RefundBankDeposit";
import RefundPreference from "./routes/RefundPreference";
import ContactDetails from "./routes/ContactDetails";
import PaymentWithTravelcard from "./routes/PaymentWithTravelcard";
import PaymentWithApp from "./routes/PaymentWithApp";
import PaymentWithBlip from "./routes/PaymentWithBlip";
import PaymentWithTicket from "./routes/PaymentWithTicket";

require("./extensions/yup-extensions");

// Store - Collecting Travel Search Data

export const travelSearchAtom = atom<TravelSearchModel>({
  selectedJourneyFrom: null,
  selectedJourneyTo: null,
  journeyDate: "",
  journeyTime: "",
});

// Store - Collecting FormData
export const formDataAtom = atom<FormModel>({
  paymentmethod: "",
  phonenumberapp: "",
  ticketnumber: "",
  cardnumber: "",
  delay: "",
  compensation: "",
  refundpreference: "",
  taxireceipt: [],
  distance: "",
  cartravelfrom: "",
  cartravelto: "",
  carlicenceplate: "",
  carexpenses: [],
  valuecodeemail: "",
  clearingnumber: "",
  accountnumber: "",
  firstname: "",
  lastname: "",
  streetaddress: "",
  zipcode: "",
  postaladdress: "",
  phonenumbercontact: "",
  emailcontact: "",
  moreinfo: "",
  journey: "",
  selectedJourney: {
    fromName: "",
    toName: "",
    date: "",
    travelTime: "",
    journeyKey: "",
    line: "",
    switches: "",
    unformattedDate: "",
  },
});

export const selectedJourneyAtom = atom({
  fromName: "",
  toName: "",
  date: "",
  travelTime: "",
  journeyKey: "",
  line: "",
  switches: "",
  unformattedDate: "",
});

// Flow guard and router setup
const FLOW_KEY = "delay-compensation-flow";

const navigationEntry = performance.getEntriesByType("navigation")[0] as
  | PerformanceNavigationTiming
  | undefined;

const isReload = navigationEntry?.type === "reload";

if (isReload) {
  sessionStorage.removeItem(FLOW_KEY);
}

function startFlow() {
  sessionStorage.setItem(FLOW_KEY, "active");
  return null;
}

function isFlowActive() {
  return sessionStorage.getItem(FLOW_KEY) === "active";
}

function clearFlow() {
  sessionStorage.removeItem(FLOW_KEY);
  return null;
}

function requireActiveFlow() {
  return isFlowActive() ? null : redirect("/");
}

function createProtectedStep(path: string, element: JSX.Element) {
  return {
    path,
    element,
    loader: async () => {
      return requireActiveFlow();
    },
  };
}

const router = createHashRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      {
        index: true,
        element: <Intro />,
        loader: () => {
          return startFlow();
        },
      },

      createProtectedStep("hur-du-reste", <TravelSearch />),
      createProtectedStep("hur-sen-blev-du", <Delay />),
      createProtectedStep("betalningssatt", <PaymentMethod />),
      createProtectedStep("med-app", <PaymentWithApp />),
      createProtectedStep("med-resekort", <PaymentWithTravelcard />),
      createProtectedStep("med-blipp", <PaymentWithBlip />),
      createProtectedStep("med-biljett", <PaymentWithTicket />),
      createProtectedStep("ersattning-for", <Compensation />),
      createProtectedStep("ersattning-for-taxi", <CompensationForTaxi />),
      createProtectedStep("ersattning-for-bil", <CompensationForCar />),
      createProtectedStep("hur-vill-du-ha-ersattning", <RefundPreference />),
      createProtectedStep("vardekod", <RefundValueCode />),
      createProtectedStep("bankinsattning", <RefundBankDeposit />),
      createProtectedStep("personuppgifter", <ContactDetails />),
      createProtectedStep("granska-och-skicka", <Summary />),

      {
        path: "tack-sida",
        element: <ThankYou />,
        loader: async () => {
          const access = requireActiveFlow();
          if (access) return access;

          clearFlow();
          return null;
        },
      },
      {
        path: "ett-fel-intraffade",
        element: <Fail />,
        loader: async () => {
          const access = requireActiveFlow();
          if (access) return access;

          clearFlow();
          return null;
        },
      },
    ],
  },
]);

// Count pages
const pagePaths =
  router?.routes[0]?.children?.map((child) => child.path).filter(Boolean) || [];

export const pageCountAtom = atom({
  pagePaths,
});

function App() {
  return <RouterProvider router={router} />;
}

export default App;
